import React from 'react';

export const POLL_DELAY = 5000;

// Polls `fn` every `every` ms while `enabled` is true. Three features hand-roll this loop (interval
// + an isCanceled flag + a terminal-stop check); this area polls in five places, so it is one hook.
//
// - Overlapping ticks are skipped: a slow response never queues a second request behind it.
// - A hidden tab pauses, and the first tick after it becomes visible runs at once, so a tab left
//   open on dev does not hammer it and a tab coming back does not wait a whole interval.
// - `fn` is called with no arguments and may be async; it is the caller's job to be idempotent and
//   to keep the previous data on a transient error (the backfill history rule).
// - Stopping is `enabled` flipping false — usually `!isTerminal(thing)` — never a count.
export const useFmdcPoll = (fn, { enabled, every = POLL_DELAY } = {}) => {
  // The newest fn, read at tick time, so callers need not memoize it to keep the interval stable.
  const fnRef = React.useRef(fn);
  fnRef.current = fn;

  React.useEffect(() => {
    if (!enabled) return undefined;

    let canceled = false;
    let busy = false;

    const tick = async () => {
      if (canceled || busy) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      busy = true;
      try {
        await fnRef.current();
      } finally {
        busy = false;
      }
    };

    const id = setInterval(tick, every);
    const onVisibility = () => {
      if (typeof document !== 'undefined' && !document.hidden) tick();
    };
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);

    return () => {
      canceled = true;
      clearInterval(id);
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, every]);
};

// A re-render every `ms` while `enabled`, for "Updated Ns ago" and elapsed labels. Returns the
// current time in ms so the label can be derived in render.
export const useNowTick = (ms = 1000, enabled = true) => {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!enabled) return undefined;
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms, enabled]);
  return now;
};
